import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Pencil, Archive, RefreshCw, X, Sparkles, Loader2, ArrowLeft, Map, Eye, Factory, Trash2, AlertTriangle } from 'lucide-react';
import API_BASE_URL from '../Config/config';
import EliteRoadmapEditor from './EliteRoadmapEditor';
import EliteGenerationMonitor from './EliteGenerationMonitor';

/**
 * EliteTechnologyManager — admin console for the Elite Technology Mastery
 * catalog (elite_technologies table).
 *
 * Phase A (24-Aug-2026) correction: this used to be a pure CRUD screen —
 * "Add Technology" just inserted a catalog-metadata row and stopped. That
 * was the wrong product behavior: it's the entry point to the TSG
 * Technology Factory. The onboarding form below only collects a
 * technology's IDENTITY (name, optional description/version/sources); TSG
 * discovers the rest via "Analyze Technology", which runs a real backend
 * Domain Intelligence pass (see technologyRoadmapService.js) and shows a
 * Technology Intelligence screen with what was actually discovered — not a
 * fabricated success response. docs_folder and the other legacy catalog
 * fields (icon/color/gradient/display order/content status) are no longer
 * required to create a technology; they're still editable via the pencil
 * icon for technologies that already have real content wired up.
 *
 * Talks to /api/admin/elite-technologies (adminAuth-protected — same
 * 'user-id' header convention as the rest of the admin console).
 *
 * Phase B (24-Aug-2026): Elite Mastery Blueprint generation and the
 * editable Roadmap review/approve screen are now real — see
 * EliteRoadmapEditor.jsx, opened via "Roadmap & Blueprint" below once a
 * technology has been analyzed. The full multi-stage Content Factory
 * (research/teaching/visual/hands-on/quiz/MCQ/interview/review/publish,
 * with a job queue + worker + generation monitor) is Phase C —
 * built 25-Aug-2026: see EliteGenerationMonitor.jsx, opened via "Content
 * Factory" once a roadmap is ROADMAP_APPROVED. Start generation → monitor
 * → review/approve topics → Publish (git + S3 + CloudFront).
 */

const getUserId = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user?.userId || user?.user_id || localStorage.getItem('user_id') || null;
  } catch {
    return localStorage.getItem('user_id') || null;
  }
};

const authHeaders = () => ({
  'Content-Type': 'application/json',
  'user-id': getUserId() || '',
});

const STATUS_OPTIONS = ['active', 'coming_soon', 'draft', 'archived'];
const CONTENT_STATUS_OPTIONS = ['legacy', 'pending_regeneration', 'generating', 'benchmarked', 'published'];
const GENERATION_PROFILE_OPTIONS = [
  { value: 'fast', label: 'Fast' },
  { value: 'balanced', label: 'Balanced' },
  { value: 'premium', label: 'Premium' },
];
const TECHNOLOGY_PRIORITY_OPTIONS = [
  { value: 'market_critical', label: 'Market Critical' },
  { value: 'important', label: 'Important' },
  { value: 'emerging', label: 'Emerging' },
  { value: 'long_tail', label: 'Long Tail' },
  { value: 'experimental', label: 'Experimental' },
];

const emptyEditForm = {
  title: '',
  description: '',
  icon_url: '',
  color: 'purple',
  gradient: 'from-[#5B21B6] to-[#7C3AED]',
  docs_folder: '',
  status: 'draft',
  is_premium_track: true,
  display_order: 100,
  content_status: 'legacy',
};

const emptyOnboardingForm = {
  title: '',
  description: '',
  version: '',
  official_doc_url: '',
  additional_source_urls: '', // one per line in the textarea, split on submit
  generation_profile: 'balanced',
  technology_priority: 'important',
};

const StatusPill = ({ status }) => {
  const tones = {
    active: 'bg-emerald-50 text-emerald-700',
    coming_soon: 'bg-amber-50 text-amber-700',
    draft: 'bg-gray-100 text-gray-600',
    archived: 'bg-red-50 text-red-600',
  };
  return (
    <span className={`inline-block text-[11px] font-bold px-2 py-0.5 rounded-full ${tones[status] || tones.draft}`}>
      {status}
    </span>
  );
};

const GENERATION_STATUS_TONES = {
  NOT_ANALYZED: 'bg-gray-100 text-gray-500',
  ANALYZING: 'bg-blue-50 text-blue-700',
  ANALYZED: 'bg-violet-50 text-violet-700',
  FAILED: 'bg-red-50 text-red-600',
  BLUEPRINT_CREATED: 'bg-violet-50 text-violet-700',
  ROADMAP_GENERATING: 'bg-blue-50 text-blue-700',
  ROADMAP_DRAFT: 'bg-amber-50 text-amber-700',
  ROADMAP_APPROVED: 'bg-teal-50 text-teal-700',
  CONTENT_PENDING: 'bg-amber-50 text-amber-700',
  GENERATING: 'bg-blue-50 text-blue-700',
  GENERATED: 'bg-teal-50 text-teal-700',
  IN_REVIEW: 'bg-amber-50 text-amber-700',
  APPROVED: 'bg-teal-50 text-teal-700',
  PUBLISHED: 'bg-emerald-50 text-emerald-700',
  OUTDATED: 'bg-orange-50 text-orange-700',
  DEPRECATED: 'bg-gray-100 text-gray-500',
};

const GenerationStatusPill = ({ status }) => (
  <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${GENERATION_STATUS_TONES[status] || GENERATION_STATUS_TONES.NOT_ANALYZED}`}>
    {status === 'ANALYZING' && <Loader2 size={11} className="animate-spin" />}
    {(status || 'NOT_ANALYZED').replace(/_/g, ' ')}
  </span>
);

const ConfidenceBar = ({ label, value }) => {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  const tone = pct >= 70 ? '#10B981' : pct >= 40 ? '#F59E0B' : '#EF4444';
  return (
    <div>
      <div className="flex items-center justify-between text-[11px] text-gray-500 mb-1">
        <span>{label}</span>
        <span className="font-bold text-gray-700">{pct}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: tone }} />
      </div>
    </div>
  );
};

const IntelligenceField = ({ label, children }) => (
  <div className="py-2 border-b border-gray-50 last:border-b-0">
    <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-0.5">{label}</div>
    <div className="text-sm text-gray-800">{children ?? <span className="text-gray-300">—</span>}</div>
  </div>
);

const jsonArray = (v) => {
  if (Array.isArray(v)) return v;
  if (typeof v === 'string') {
    try { const parsed = JSON.parse(v); return Array.isArray(parsed) ? parsed : []; } catch { return []; }
  }
  return [];
};

const EliteTechnologyManager = () => {
  const [technologies, setTechnologies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Legacy catalog-metadata edit modal (icon/color/docs_folder/etc.) —
  // unchanged in spirit, still needed for technologies with real content.
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingSlug, setEditingSlug] = useState(null);
  const [editForm, setEditForm] = useState(emptyEditForm);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Technology Onboarding (spec §2) — replaces the old "Add Technology" form.
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingForm, setOnboardingForm] = useState(emptyOnboardingForm);
  const [analyzing, setAnalyzing] = useState(false);
  const [onboardingError, setOnboardingError] = useState(null);

  // Technology Intelligence screen (spec §4)
  const [intelligence, setIntelligence] = useState(null); // { technology, domainIntelligence, estimate }
  const [intelligenceLoading, setIntelligenceLoading] = useState(false);
  // Phase B: Roadmap & Blueprint editor screen (spec §5-9) — opened on top
  // of the Technology Intelligence screen for an already-analyzed technology.
  // Blueprint generation, Roadmap generation/editing, and Approve all live
  // in EliteRoadmapEditor.jsx now — this screen no longer duplicates any of
  // that generation logic itself.
  const [showRoadmapEditor, setShowRoadmapEditor] = useState(false);
  const [showMonitor, setShowMonitor] = useState(false); // Phase C Content Factory screen
  // URL ↔ screen sync so the full-screen Content Workspace can return to the
  // exact roadmap context (/superadmin/elite-technologies?tech=<slug>&view=monitor).
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkHandled = useRef(false);
  // 26-Aug-2026: permanent deletion of an archived technology. `deleteTarget`
  // holds the slug being deleted; `deleteImpact` is the server's report of
  // exactly what would be destroyed, fetched before we ask for confirmation.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteImpact, setDeleteImpact] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/elite-technologies`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setTechnologies(data.technologies || []);
    } catch (err) {
      console.error('Failed to load elite technologies:', err);
      setError('Failed to load technologies. You may need admin access.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // --- Legacy edit (catalog metadata) ---------------------------------

  const openEdit = (tech) => {
    setEditingSlug(tech.slug);
    setEditForm({
      title: tech.title || '',
      description: tech.description || '',
      icon_url: tech.icon_url || '',
      color: tech.color || 'purple',
      gradient: tech.gradient || 'from-[#5B21B6] to-[#7C3AED]',
      docs_folder: tech.docs_folder || '',
      status: tech.status || 'draft',
      is_premium_track: !!tech.is_premium_track,
      display_order: tech.display_order ?? 100,
      content_status: tech.content_status || 'legacy',
    });
    setSaveError(null);
    setShowEditForm(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/elite-technologies/${editingSlug}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(editForm),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      setShowEditForm(false);
      await load();
    } catch (err) {
      setSaveError(err.message || 'Failed to save technology');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (slug) => {
    if (!window.confirm(`Archive "${slug}"? It will disappear from the learner picker.`)) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/elite-technologies/${slug}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await load();
    } catch (err) {
      console.error('Failed to archive technology:', err);
      window.alert('Failed to archive technology.');
    }
  };

  /** Open the permanent-delete dialog and load what would actually be lost. */
  const openDelete = async (slug) => {
    setDeleteTarget(slug); setDeleteImpact(null); setDeleteConfirm(''); setDeleteBusy(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/elite-technologies/${slug}/deletion-impact`, { headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) throw new Error(data.error || `HTTP ${res.status}`);
      setDeleteImpact(data);
    } catch (err) {
      setDeleteImpact({ error: err.message || 'Failed to load deletion impact' });
    } finally {
      setDeleteBusy(false);
    }
  };

  const closeDelete = () => { setDeleteTarget(null); setDeleteImpact(null); setDeleteConfirm(''); };

  const handleDeletePermanently = async () => {
    setDeleteBusy(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/elite-technologies/${deleteTarget}/delete-permanent`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ confirmSlug: deleteConfirm.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) throw new Error(data.error || `HTTP ${res.status}`);
      closeDelete();
      await load();
    } catch (err) {
      window.alert(err.message || 'Failed to delete technology.');
    } finally {
      setDeleteBusy(false);
    }
  };

  // --- Technology Onboarding + Analyze (spec §2-4) ---------------------

  const openOnboarding = () => {
    setOnboardingForm(emptyOnboardingForm);
    setOnboardingError(null);
    setShowOnboarding(true);
  };

  /** Runs the real backend analysis for a slug that already has an identity
   * record (either just created, or an existing NOT_ANALYZED/FAILED row from
   * the table's "Analyze" action) and opens the Technology Intelligence
   * screen with whatever the backend actually returns. */
  const runAnalysis = async (slug) => {
    const res = await fetch(`${API_BASE_URL}/api/admin/elite-technologies/${slug}/domain-intelligence`, {
      method: 'POST',
      headers: authHeaders(),
    });
    const data = await res.json();
    if (!res.ok || !data.success) {
      throw new Error(data.error || `HTTP ${res.status}`);
    }
    setIntelligence({ technology: data.technology, domainIntelligence: data.domainIntelligence, estimate: data.estimate });
    setShowRoadmapEditor(false);
  };

  const handleAnalyzeTechnology = async (e) => {
    e.preventDefault();
    if (!onboardingForm.title.trim()) {
      setOnboardingError('Technology Name is required.');
      return;
    }
    setAnalyzing(true);
    setOnboardingError(null);
    try {
      const additionalSourceUrls = onboardingForm.additional_source_urls
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);

      const createRes = await fetch(`${API_BASE_URL}/api/admin/elite-technologies`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          title: onboardingForm.title.trim(),
          description: onboardingForm.description.trim() || null,
          version: onboardingForm.version.trim() || null,
          official_doc_url: onboardingForm.official_doc_url.trim() || null,
          additional_source_urls: additionalSourceUrls,
          generation_profile: onboardingForm.generation_profile,
          technology_priority: onboardingForm.technology_priority,
        }),
      });
      const createData = await createRes.json();
      if (!createRes.ok || !createData.success) {
        throw new Error(createData.error || `HTTP ${createRes.status}`);
      }

      await runAnalysis(createData.technology.slug);
      setShowOnboarding(false);
      await load();
    } catch (err) {
      setOnboardingError(err.message || 'Failed to analyze technology');
    } finally {
      setAnalyzing(false);
    }
  };

  /** Table row action for an existing technology that hasn't been analyzed
   * yet (or failed) — re-runs analysis without going through onboarding. */
  const handleAnalyzeExisting = async (slug) => {
    setIntelligenceLoading(true);
    try {
      await runAnalysis(slug);
      await load();
    } catch (err) {
      window.alert(`Analysis failed: ${err.message || err}`);
    } finally {
      setIntelligenceLoading(false);
    }
  };

  /** Table row action / intelligence-screen refresh for an already-analyzed
   * technology — just re-fetches, does not re-run the AI analysis. */
  const handleViewIntelligence = async (slug) => {
    setIntelligenceLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/elite-technologies/${slug}/domain-intelligence`, {
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      setIntelligence({ technology: data.technology, domainIntelligence: data.domainIntelligence, estimate: data.estimate });
      setShowRoadmapEditor(false);
    } catch (err) {
      window.alert(`Failed to load Technology Intelligence: ${err.message || err}`);
    } finally {
      setIntelligenceLoading(false);
    }
  };

  // Deep link: ?tech=<slug>&view=monitor (what the Content Workspace's
  // "Back to Roadmap" and browser Back land on) re-opens the Generation Monitor.
  useEffect(() => {
    if (deepLinkHandled.current) return;
    deepLinkHandled.current = true;
    const tech = searchParams.get('tech');
    const view = searchParams.get('view');
    if (!tech) return;
    (async () => {
      await handleViewIntelligence(tech);
      if (view === 'monitor') setShowMonitor(true);
      else if (view === 'roadmap') setShowRoadmapEditor(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the URL in step with the screen (replace, not push — no history spam).
  useEffect(() => {
    const slug = intelligence?.technology?.slug;
    const view = !slug ? null : showMonitor ? 'monitor' : showRoadmapEditor ? 'roadmap' : 'intelligence';
    const next = new URLSearchParams();
    if (slug) { next.set('tech', slug); next.set('view', view); }
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intelligence?.technology?.slug, showMonitor, showRoadmapEditor]);

  // -----------------------------------------------------------------------
  // Technology Intelligence screen (spec §4) — replaces the table view
  // while open.
  // -----------------------------------------------------------------------
  if (intelligence && showMonitor) {
    return (
      <EliteGenerationMonitor
        slug={intelligence.technology.slug}
        onBack={() => setShowMonitor(false)}
        onTechnologyChange={(tech) => setIntelligence((prev) => (prev ? { ...prev, technology: tech } : prev))}
      />
    );
  }

  if (intelligence && showRoadmapEditor) {
    return (
      <EliteRoadmapEditor
        slug={intelligence.technology.slug}
        onBack={() => setShowRoadmapEditor(false)}
        onTechnologyChange={(tech) => setIntelligence((prev) => (prev ? { ...prev, technology: tech } : prev))}
      />
    );
  }

  if (intelligence) {
    const { technology: tech, domainIntelligence: di, estimate } = intelligence;
    const aliases = jsonArray(di?.aliases);
    const sources = jsonArray(di?.official_sources);
    const prerequisites = jsonArray(di?.prerequisites);
    const competencies = jsonArray(di?.competency_model);

    return (
      <div className="min-h-screen bg-[#F6F7FB] p-6">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => { setIntelligence(null); setShowRoadmapEditor(false); setShowMonitor(false); }}
            className="mb-4 flex items-center gap-1 text-sm font-semibold text-gray-500 hover:text-gray-800"
          >
            <ArrowLeft size={16} /> Back to Elite Technologies
          </button>

          <div className="flex items-start justify-between mb-4">
            <div>
              <h1 className="text-2xl font-black text-gray-900">{tech?.title}</h1>
              <div className="mt-1 flex items-center gap-2">
                <GenerationStatusPill status={tech?.generation_status} />
                <StatusPill status={tech?.status} />
              </div>
            </div>
            {intelligenceLoading && <Loader2 size={20} className="animate-spin text-violet-500" />}
          </div>

          {!di ? (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center text-gray-400 text-sm">
              No Domain Intelligence yet for this technology.
              <div className="mt-3">
                <button
                  onClick={() => handleAnalyzeExisting(tech.slug)}
                  disabled={intelligenceLoading}
                  className="px-4 py-2 rounded-lg text-white text-sm font-bold shadow inline-flex items-center gap-2 disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg,#5B21B6,#7C3AED)' }}
                >
                  <Sparkles size={16} /> Analyze Technology
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 md:col-span-2">
                  <h2 className="text-xs font-black text-gray-400 uppercase tracking-wide mb-1">Technology Identity</h2>
                  <IntelligenceField label="Type">{di.technology_type}</IntelligenceField>
                  <IntelligenceField label="Domain">{di.domain}</IntelligenceField>
                  <IntelligenceField label="Subdomain">{di.subdomain}</IntelligenceField>
                  <IntelligenceField label="Ecosystem">{di.ecosystem}</IntelligenceField>
                  <IntelligenceField label="Version">{tech.version || di.version_notes}</IntelligenceField>
                  <IntelligenceField label="Aliases">
                    {aliases.length ? aliases.join(', ') : null}
                  </IntelligenceField>
                  <IntelligenceField label={`Official Sources (${di.source_count ?? sources.length})`}>
                    {sources.length ? (
                      <ul className="space-y-1">
                        {sources.map((s, i) => (
                          <li key={i}>
                            <a href={s.url} target="_blank" rel="noreferrer" className="text-violet-600 hover:underline break-all">
                              {s.title || s.url}
                            </a>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </IntelligenceField>
                  <IntelligenceField label="Prerequisites">
                    {prerequisites.length ? prerequisites.join(', ') : null}
                  </IntelligenceField>
                  <IntelligenceField label="Core Competencies">
                    {competencies.length ? (
                      <ul className="list-disc pl-4 space-y-0.5">
                        {competencies.map((c, i) => <li key={i}><b>{c.name}:</b> {c.description}</li>)}
                      </ul>
                    ) : null}
                  </IntelligenceField>
                  <IntelligenceField label="Production Relevance">{di.production_relevance_notes}</IntelligenceField>
                  <IntelligenceField label="Interview Relevance">{di.interview_relevance_notes}</IntelligenceField>
                  <IntelligenceField label="Career Relevance">{di.career_relevance_notes}</IntelligenceField>
                </div>

                <div className="space-y-4">
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                    <h2 className="text-xs font-black text-gray-400 uppercase tracking-wide mb-3">Confidence</h2>
                    <div className="space-y-3">
                      <ConfidenceBar label="Technology" value={di.technology_confidence} />
                      <ConfidenceBar label="Source" value={di.source_confidence} />
                      <ConfidenceBar label="Domain" value={di.domain_confidence} />
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                    <h2 className="text-xs font-black text-gray-400 uppercase tracking-wide mb-3">Generation Plan (estimate)</h2>
                    <IntelligenceField label="Domain Intelligence Status">
                      <GenerationStatusPill status={tech.generation_status} />
                    </IntelligenceField>
                    <IntelligenceField label="Blueprint & Roadmap">
                      <button
                        onClick={() => setShowRoadmapEditor(true)}
                        className="text-violet-600 hover:underline font-semibold"
                      >
                        Open Roadmap Editor →
                      </button>
                    </IntelligenceField>
                    <IntelligenceField label="Estimated Sections">{estimate?.estimatedSections}</IntelligenceField>
                    <IntelligenceField label="Estimated Topics">{estimate?.estimatedTopics}</IntelligenceField>
                    <IntelligenceField label="Estimated Generation Cost">
                      {estimate ? `~$${estimate.estimatedCostUsd.toFixed(2)}` : null}
                    </IntelligenceField>
                    <IntelligenceField label="Estimated Generation Duration">
                      {estimate ? `~${Math.round(estimate.estimatedDurationMinutes / 60 * 10) / 10} hr` : null}
                    </IntelligenceField>
                    <p className="text-[10px] text-gray-400 mt-1">{estimate?.note}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex items-center justify-between">
                <div>
                  <h2 className="text-xs font-black text-gray-400 uppercase tracking-wide mb-1">Elite Mastery Blueprint & Roadmap</h2>
                  <p className="text-xs text-gray-500">
                    Generate the Blueprint, plan and edit the Roadmap, and approve it — all in one screen.
                  </p>
                </div>
                <button
                  onClick={() => setShowRoadmapEditor(true)}
                  className="px-4 py-2.5 rounded-lg text-white text-xs font-bold shadow inline-flex items-center gap-1.5 shrink-0"
                  style={{ background: 'linear-gradient(135deg,#5B21B6,#7C3AED)' }}
                >
                  <Map size={14} /> Open Roadmap Editor
                </button>
              </div>

              {/* Phase C: Content Factory — after Approve Roadmap */}
              {(() => {
                const gs = tech?.generation_status;
                const ready = ['ROADMAP_APPROVED', 'CONTENT_PENDING', 'GENERATING', 'GENERATED', 'IN_REVIEW', 'APPROVED', 'PUBLISHED', 'OUTDATED'].includes(gs);
                const hint = gs === 'ROADMAP_APPROVED' ? 'Roadmap approved — content generation can start.'
                  : gs === 'GENERATING' ? 'Content is being generated on the server right now.'
                  : gs === 'GENERATED' ? 'All topics generated — review and publish.'
                  : gs === 'IN_REVIEW' ? 'Some topics need review (low quality score or failed) before publishing.'
                  : gs === 'APPROVED' ? 'Content approved — ready to publish.'
                  : gs === 'PUBLISHED' ? 'Published and live for learners.'
                  : 'Approve the roadmap first (Roadmap Editor → Approve Roadmap).';
                return (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mt-4 flex items-center justify-between">
                    <div>
                      <h2 className="text-xs font-black text-gray-400 uppercase tracking-wide mb-1">Content Factory</h2>
                      <p className="text-xs text-gray-500">
                        Generate every roadmap topic as a full Elite document (research → teaching → production → interview → hands-on → mastery → review), monitor progress, review, and publish. {hint}
                      </p>
                    </div>
                    <button
                      onClick={() => setShowMonitor(true)}
                      disabled={!ready}
                      title={ready ? '' : 'Approve the roadmap first'}
                      className="px-4 py-2.5 rounded-lg text-white text-xs font-bold shadow inline-flex items-center gap-1.5 shrink-0 disabled:opacity-40"
                      style={{ background: 'linear-gradient(135deg,#059669,#10B981)' }}
                    >
                      <Factory size={14} /> {gs === 'ROADMAP_APPROVED' ? 'Start Content Generation' : 'Open Content Factory'}
                    </button>
                  </div>
                );
              })()}
            </>
          )}
        </div>
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Table view
  // -----------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-[#F6F7FB] p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-black text-gray-900">Elite Technologies</h1>
            <p className="text-sm text-gray-500 mt-1">
              Add a technology and TSG analyzes it — you don't need to provide curriculum, topics, or
              content up front. Point <code>docs_folder</code> at an existing <code>public/docs/&lt;folder&gt;</code>{' '}
              tree separately, once real content exists.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={load}
              className="px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 flex items-center gap-2 text-sm font-semibold"
            >
              <RefreshCw size={16} /> Refresh
            </button>
            <button
              onClick={openOnboarding}
              className="px-4 py-2 rounded-lg text-white flex items-center gap-2 text-sm font-bold shadow"
              style={{ background: 'linear-gradient(135deg,#5B21B6,#7C3AED)' }}
            >
              <Plus size={16} /> Add Technology
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm rounded">
            {error}
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 uppercase text-[11px] tracking-wide">
              <tr>
                <th className="text-left px-4 py-3">Technology</th>
                <th className="text-left px-4 py-3">Slug</th>
                <th className="text-left px-4 py-3">Generation</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Priority</th>
                <th className="text-left px-4 py-3">Order</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">Loading…</td></tr>
              ) : technologies.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No technologies yet.</td></tr>
              ) : (
                technologies.map((tech) => {
                  const genStatus = tech.generation_status || 'NOT_ANALYZED';
                  const canAnalyze = genStatus === 'NOT_ANALYZED' || genStatus === 'FAILED';
                  const canViewIntelligence = !canAnalyze && genStatus !== 'ANALYZING';
                  const canOpenFactory = ['ROADMAP_APPROVED', 'CONTENT_PENDING', 'GENERATING', 'GENERATED', 'IN_REVIEW', 'APPROVED', 'PUBLISHED', 'OUTDATED'].includes(genStatus);
                  return (
                    <tr key={tech.slug} className="border-t border-gray-100">
                      <td className="px-4 py-3 font-semibold text-gray-900">{tech.title}</td>
                      <td className="px-4 py-3 text-gray-500 font-mono text-xs">{tech.slug}</td>
                      <td className="px-4 py-3"><GenerationStatusPill status={genStatus} /></td>
                      <td className="px-4 py-3"><StatusPill status={tech.status} /></td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{(tech.technology_priority || '').replace('_', ' ')}</td>
                      <td className="px-4 py-3 text-gray-500">{tech.display_order}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {canAnalyze && (
                          <button
                            onClick={() => handleAnalyzeExisting(tech.slug)}
                            disabled={intelligenceLoading}
                            className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg hover:bg-violet-50 text-violet-600 text-xs font-bold mr-1 disabled:opacity-50"
                            title="Analyze Technology"
                          >
                            <Sparkles size={14} /> Analyze
                          </button>
                        )}
                        {canViewIntelligence && (
                          <button
                            onClick={() => handleViewIntelligence(tech.slug)}
                            disabled={intelligenceLoading}
                            className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg hover:bg-violet-50 text-violet-600 text-xs font-bold mr-1 disabled:opacity-50"
                            title="View Intelligence"
                          >
                            <Eye size={14} /> Intelligence
                          </button>
                        )}
                        {canOpenFactory && (
                          <button
                            onClick={async () => { await handleViewIntelligence(tech.slug); setShowMonitor(true); }}
                            disabled={intelligenceLoading}
                            className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg hover:bg-emerald-50 text-emerald-700 text-xs font-bold mr-1 disabled:opacity-50"
                            title={genStatus === 'GENERATING' ? 'Content generation in progress — open monitor' : 'Content Factory'}
                          >
                            {genStatus === 'GENERATING' ? <Loader2 size={14} className="animate-spin" /> : <Factory size={14} />} Content
                          </button>
                        )}
                        <button
                          onClick={() => openEdit(tech)}
                          className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 mr-1"
                          title="Edit catalog metadata"
                        >
                          <Pencil size={16} />
                        </button>
                        {tech.status !== 'archived' ? (
                          <button
                            onClick={() => handleArchive(tech.slug)}
                            className="p-2 rounded-lg hover:bg-red-50 text-red-500"
                            title="Archive"
                          >
                            <Archive size={16} />
                          </button>
                        ) : (
                          <button
                            onClick={() => openDelete(tech.slug)}
                            className="p-2 rounded-lg hover:bg-red-50 text-red-600"
                            title="Delete permanently — this cannot be undone"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          </div>
        </div>
      </div>

      {/* Technology Onboarding modal (spec §2) */}
      {showOnboarding && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="font-bold text-lg text-gray-900">Add Technology</h2>
              <button onClick={() => setShowOnboarding(false)} className="p-1 text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleAnalyzeTechnology} className="p-6 space-y-4">
              <p className="text-xs text-gray-500 -mt-1">
                Enter the technology's identity — TSG discovers its type, domain, ecosystem, and a
                learning roadmap automatically. You don't need to provide topics, curriculum, or content.
              </p>

              {onboardingError && (
                <div className="p-3 bg-red-50 border-l-4 border-red-500 text-red-700 text-xs rounded">
                  {onboardingError}
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-gray-500 mb-1">Technology Name *</label>
                <input
                  type="text"
                  required
                  autoFocus
                  placeholder="e.g. Apache Iceberg, LangGraph, Rust, MCP"
                  value={onboardingForm.title}
                  onChange={(e) => setOnboardingForm({ ...onboardingForm, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 mb-1">Description</label>
                <textarea
                  rows={2}
                  placeholder="Optional — helps Analyze, doesn't replace it"
                  value={onboardingForm.description}
                  onChange={(e) => setOnboardingForm({ ...onboardingForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1">Version</label>
                  <input
                    type="text"
                    placeholder="e.g. 1.6"
                    value={onboardingForm.version}
                    onChange={(e) => setOnboardingForm({ ...onboardingForm, version: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1">Official Documentation URL</label>
                  <input
                    type="url"
                    placeholder="https://…"
                    value={onboardingForm.official_doc_url}
                    onChange={(e) => setOnboardingForm({ ...onboardingForm, official_doc_url: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 mb-1">Additional Source URLs</label>
                <textarea
                  rows={2}
                  placeholder={'One per line, e.g.\nhttps://github.com/apache/iceberg'}
                  value={onboardingForm.additional_source_urls}
                  onChange={(e) => setOnboardingForm({ ...onboardingForm, additional_source_urls: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 mb-1">Source Documents</label>
                <div className="w-full px-3 py-2 border border-dashed border-gray-200 rounded-lg text-xs text-gray-400 bg-gray-50">
                  Upload isn't wired to storage yet — use the source URLs above for now.
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1">Generation Profile</label>
                  <select
                    value={onboardingForm.generation_profile}
                    onChange={(e) => setOnboardingForm({ ...onboardingForm, generation_profile: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  >
                    {GENERATION_PROFILE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1">Technology Priority</label>
                  <select
                    value={onboardingForm.technology_priority}
                    onChange={(e) => setOnboardingForm({ ...onboardingForm, technology_priority: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  >
                    {TECHNOLOGY_PRIORITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={analyzing}
                  className="flex-1 py-3 rounded-xl font-bold text-white text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2"
                  style={{ background: 'linear-gradient(135deg,#5B21B6,#7C3AED)' }}
                >
                  {analyzing ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                  {analyzing ? 'Analyzing…' : 'Analyze Technology'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowOnboarding(false)}
                  disabled={analyzing}
                  className="flex-1 py-3 rounded-xl font-bold text-gray-600 bg-gray-100 text-sm disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Legacy catalog-metadata edit modal — unchanged behavior, still used
          for wiring up docs_folder/icon/etc. once real content exists. */}
      {showEditForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="font-bold text-lg text-gray-900">Edit {editingSlug}</h2>
              <button onClick={() => setShowEditForm(false)} className="p-1 text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSaveEdit} className="p-6 space-y-4">
              {saveError && (
                <div className="p-3 bg-red-50 border-l-4 border-red-500 text-red-700 text-xs rounded">
                  {saveError}
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-gray-500 mb-1">Title *</label>
                <input
                  type="text"
                  required
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 mb-1">Description</label>
                <textarea
                  rows={2}
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 mb-1">Docs Folder</label>
                <input
                  type="text"
                  placeholder="e.g. Kubernetes (folder under public/docs/)"
                  value={editForm.docs_folder}
                  onChange={(e) => setEditForm({ ...editForm, docs_folder: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
                <p className="text-[11px] text-gray-400 mt-1">
                  Must match a folder under skillgenpro-frontend/public/docs/ containing a
                  mapping.json. Leave blank until real content exists.
                </p>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 mb-1">Icon URL</label>
                <input
                  type="text"
                  value={editForm.icon_url}
                  onChange={(e) => setEditForm({ ...editForm, icon_url: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1">Status</label>
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  >
                    {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1">Content Status</label>
                  <select
                    value={editForm.content_status}
                    onChange={(e) => setEditForm({ ...editForm, content_status: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  >
                    {CONTENT_STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1">Display Order</label>
                  <input
                    type="number"
                    value={editForm.display_order}
                    onChange={(e) => setEditForm({ ...editForm, display_order: parseInt(e.target.value, 10) || 0 })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-xs font-bold text-gray-500">
                    <input
                      type="checkbox"
                      checked={editForm.is_premium_track}
                      onChange={(e) => setEditForm({ ...editForm, is_premium_track: e.target.checked })}
                    />
                    Premium track
                  </label>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 py-3 rounded-xl font-bold text-white text-sm disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg,#5B21B6,#7C3AED)' }}
                >
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowEditForm(false)}
                  className="flex-1 py-3 rounded-xl font-bold text-gray-600 bg-gray-100 text-sm"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Permanent deletion of an archived technology (26-Aug-2026).
          Shows the server's impact report first, and only enables the button
          once the admin has typed the slug — this is irreversible. */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-5">
              <div className="flex items-start justify-between mb-3">
                <h2 className="text-lg font-black text-gray-900 flex items-center gap-2">
                  <AlertTriangle size={18} className="text-red-500" /> Delete permanently
                </h2>
                <button onClick={closeDelete} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
              </div>

              {deleteBusy && !deleteImpact ? (
                <div className="py-10 flex justify-center"><Loader2 size={22} className="animate-spin text-violet-500" /></div>
              ) : deleteImpact?.error ? (
                <div className="p-3 bg-red-50 border-l-4 border-red-500 text-red-700 text-xs rounded">{deleteImpact.error}</div>
              ) : deleteImpact ? (
                <>
                  <p className="text-sm text-gray-600 mb-3">
                    <span className="font-bold">{deleteImpact.technology?.title}</span>{' '}
                    (<code className="text-xs">{deleteTarget}</code>) and everything generated for it will be removed. This cannot be undone.
                  </p>

                  {!deleteImpact.canDelete && (
                    <div className="p-3 bg-amber-50 border-l-4 border-amber-500 text-amber-800 text-xs rounded mb-3">
                      {deleteImpact.blockedReason}
                    </div>
                  )}

                  <div className="rounded-lg border border-gray-100 divide-y divide-gray-100 text-xs mb-3">
                    {[
                      ['Sections', deleteImpact.cascades?.sections],
                      ['Topics', deleteImpact.cascades?.topics],
                      ['Generated topic documents', deleteImpact.cascades?.topicContent],
                      ['…of which published', deleteImpact.cascades?.publishedTopics],
                      ['Content runs', deleteImpact.cascades?.runs],
                      ['Generation jobs', deleteImpact.cascades?.jobs],
                      ['Publication records', deleteImpact.cascades?.publications],
                    ].filter(([, v]) => v !== null && v !== undefined).map(([label, v]) => (
                      <div key={label} className="flex justify-between px-3 py-1.5">
                        <span className="text-gray-500">{label}</span>
                        <span className="font-bold text-gray-800">{v}</span>
                      </div>
                    ))}
                  </div>

                  {deleteImpact.learnerImpact?.selectionsCleared > 0 && (
                    <p className="text-xs text-amber-700 mb-2">
                      <b>{deleteImpact.learnerImpact.selectionsCleared}</b> learner(s) currently have this selected. Their
                      selection is cleared so they can choose again — otherwise they would be stuck on a dead page.
                    </p>
                  )}
                  <p className="text-[11px] text-gray-500 mb-2">
                    Kept: <b>{(() => { const c = deleteImpact.preserved?.costUsd ?? 0; return c > 0 && c < 0.01 ? '<$0.01' : `$${c.toFixed(2)}`; })()}</b> of AI spend history, so the generation
                    budget cap stays accurate.
                    {deleteImpact.learnerImpact?.votesKept ? ` ${deleteImpact.learnerImpact.votesKept} vote(s) kept.` : ''}
                  </p>
                  {deleteImpact.filesNote && (
                    <p className="text-[11px] text-gray-500 mb-3">{deleteImpact.filesNote}</p>
                  )}

                  <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-1">
                    Type <code className="text-red-600">{deleteTarget}</code> to confirm
                  </label>
                  <input
                    autoFocus
                    value={deleteConfirm}
                    onChange={(e) => setDeleteConfirm(e.target.value)}
                    disabled={!deleteImpact.canDelete}
                    placeholder={deleteTarget}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm mb-4 disabled:bg-gray-50"
                  />

                  <div className="flex gap-2">
                    <button
                      onClick={handleDeletePermanently}
                      disabled={deleteBusy || !deleteImpact.canDelete || deleteConfirm.trim() !== deleteTarget}
                      className="flex-1 py-2.5 rounded-xl font-bold text-white text-sm bg-red-600 hover:bg-red-700 disabled:opacity-40 inline-flex items-center justify-center gap-2"
                    >
                      {deleteBusy ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />} Delete permanently
                    </button>
                    <button onClick={closeDelete} className="flex-1 py-2.5 rounded-xl font-bold text-gray-600 bg-gray-100 text-sm">
                      Cancel
                    </button>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EliteTechnologyManager;
